import { router } from '@inertiajs/react'
import Layout from '@/components/layout/Layout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FileText, Columns3 } from 'lucide-react'

interface RecentPage {
  id: string
  title: string
  slug: string
  tags: string[]
  excerpt: string | null
  updated_at: string
}

interface BoardSummary {
  id: string
  name: string
  slug: string
  description: string | null
  column_count: number
  created_at: string
}

interface Props {
  recent_pages: RecentPage[]
  boards: BoardSummary[]
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const hours = Math.floor(diff / 3600000)
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  return `${days}d ago`
}

export default function DashboardIndex({ recent_pages, boards }: Props) {
  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>

        <div className="grid gap-6 md:grid-cols-2">
          <Card className="border-gray-200 bg-white">
            <CardHeader>
              <CardTitle className="text-gray-900">Recent Pages</CardTitle>
            </CardHeader>
            <CardContent>
              {recent_pages.length === 0 ? (
                <p className="text-sm text-gray-500">No pages yet. Create your first page to get started.</p>
              ) : (
                <div className="space-y-1">
                  {recent_pages.map(page => (
                    <button
                      key={page.id}
                      onClick={() => router.visit(`/notes/${page.slug}`)}
                      className="flex items-start gap-2 w-full rounded-md px-2 py-1.5 text-left hover:bg-gray-50 transition-colors"
                    >
                      <FileText className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{page.title}</div>
                        {page.excerpt && (
                          <div className="text-xs text-gray-400 truncate">{page.excerpt}</div>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5">{timeAgo(page.updated_at)}</span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-gray-200 bg-white">
            <CardHeader>
              <CardTitle className="text-gray-900">Boards</CardTitle>
            </CardHeader>
            <CardContent>
              {boards.length === 0 ? (
                <p className="text-sm text-gray-500">No boards yet. Create a board to track tasks.</p>
              ) : (
                <div className="space-y-1">
                  {boards.map(board => (
                    <button
                      key={board.id}
                      onClick={() => router.visit(`/boards/${board.slug}`)}
                      className="flex items-start gap-2 w-full rounded-md px-2 py-1.5 text-left hover:bg-gray-50 transition-colors"
                    >
                      <Columns3 className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{board.name}</div>
                        {board.description && (
                          <div className="text-xs text-gray-400 truncate">{board.description}</div>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5">{board.column_count} cols</span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  )
}
